import { createBrowserRouter, RouterProvider } from "react-router-dom";
import NextTopLoader from "nextjs-toploader";
import colors from "tailwindcss/colors";
import { Suspense, useEffect } from "react";
import { ApplicationLoading } from "./routes/application-loading";

import { PilotAccessProvider } from "./components/pilot/PilotAccessProvider";
import { Routes } from "./routes/routes";
import {
  DrawerProvider,
  PermissionProvider,
} from "./providers";
import { useAuthStore } from "./stores/use-auth-store";
import ErrorBoundary from "./components/error-boundary";
import { ConnectionHealthContext, useConnectionHealthState } from "./hooks/use-connection-health";
import { CommandCenterMentionBridge } from "@/components/command-center/CommandCenterMentionBridge";
import { HitlPusherBridge } from "@/components/generative-ui/hitl/HitlPusherBridge";
import { UsageLimitModal } from "@/components/billing/UsageLimitModal";
// Side-effect imports: register card components with the global CardRegistry
// at boot. Without these, parseEnvelope returns envelopes whose `card` field
// references unregistered kinds and renderCard returns null — every cowork turn
// would fall through to the legacy switch fallback.
import "@/components/generative-ui/core";
import "@/components/generative-ui/forms";
import "@/components/generative-ui/draft";
// Observes identity invalidation before the drawer first mounts: form-set request-key guards are cleared on sign-out or a user/tenant switch.
import "@/components/cowork/form-set-guard";
import "@/components/generative-ui/sdr";

const basename = import.meta.env.VITE_BASE_URL;
const router = createBrowserRouter([{
  path: "*",
  element: <ErrorBoundary>
    <NextTopLoader color={colors.teal[400]} />
    <HitlPusherBridge />
    <CommandCenterMentionBridge />
    <UsageLimitModal />
    <Routes />
  </ErrorBoundary>,
}], { basename });

export function App() {
  const connectionStatus = useConnectionHealthState();

  useEffect(() => {
    // Initialize Auth Session
    useAuthStore.getState().initialize();
  }, []);

  return (
    <ErrorBoundary>
      <Suspense fallback={<ApplicationLoading />}>
      <ConnectionHealthContext.Provider value={connectionStatus}>
          <DrawerProvider>
            <PilotAccessProvider><PermissionProvider>
              <RouterProvider router={router} />
            </PermissionProvider></PilotAccessProvider>
          </DrawerProvider>
      </ConnectionHealthContext.Provider>
      </Suspense>
    </ErrorBoundary>
  );
}
